import {
  ContainerTile,
  Wrapper,
  Button,
  Tile,
  RowWrapper,
  SubText,
  H1,
} from "components/styled-components/generic";
import {
  Thumb,
  Track,
  SliderContainer,
} from "components/styled-components/slider";
import { Range } from "react-range";
import { FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { RoadsStatus } from "shared-types";
import { colorPrimary } from "components/styled-components/colors";

interface RoadsProps {
  setCountryRoads: (p: RoadsStatus) => void;
  countryRoads: RoadsStatus;
  setHighwayRoads: (p: RoadsStatus) => void;
  highwayRoads: RoadsStatus;
  setCityRoads: (p: RoadsStatus) => void;
  cityRoads: RoadsStatus;
}

export const Roads: FC<RoadsProps> = ({
  setCountryRoads,
  countryRoads,
  setHighwayRoads,
  highwayRoads,
  setCityRoads,
  cityRoads,
}) => {
  const sliderRange = useMemo(() => {
    return [
      countryRoads.isChosen,
      highwayRoads.isChosen,
      cityRoads.isChosen,
    ].filter((pref) => pref).length;
  }, [countryRoads.isChosen, highwayRoads.isChosen, cityRoads.isChosen]);

  useEffect(() => {
    switch (sliderRange) {
      case 1:
        if (countryRoads.isChosen) {
          setCountryRoads({ isChosen: true, percentage: 100 });
          setHighwayRoads({ isChosen: false, percentage: 0 });
          setCityRoads({ isChosen: false, percentage: 0 });
        } else if (highwayRoads.isChosen) {
          setHighwayRoads({
            isChosen: true,
            percentage: 100,
          });
          setCountryRoads({ isChosen: false, percentage: 0 });
          setCityRoads({ isChosen: false, percentage: 0 });
        } else {
          setCityRoads({
            isChosen: true,
            percentage: 100,
          });
          setHighwayRoads({ isChosen: false, percentage: 0 });
          setCountryRoads({ isChosen: false, percentage: 0 });
        }
        break;
      case 2:
        if (countryRoads.isChosen && highwayRoads.isChosen) {
          setCountryRoads({ isChosen: true, percentage: 50 });
          setHighwayRoads({ isChosen: true, percentage: 50 });
          setCityRoads({ isChosen: false, percentage: 0 });
        } else if (countryRoads.isChosen && cityRoads.isChosen) {
          setCountryRoads({ isChosen: true, percentage: 50 });
          setHighwayRoads({ isChosen: false, percentage: 0 });
          setCityRoads({ isChosen: true, percentage: 50 });
        } else {
          setCountryRoads({ isChosen: false, percentage: 0 });
          setHighwayRoads({ isChosen: true, percentage: 50 });
          setCityRoads({ isChosen: true, percentage: 50 });
        }
        break;
    }
  }, [sliderRange, setCountryRoads, setHighwayRoads, setCityRoads]); //eslint-disable-line

  const onRoadClick = (roadType: string) => {
    switch (roadType) {
      case "high":
        setHighwayRoads({
          ...highwayRoads,
          isChosen: !highwayRoads.isChosen,
        });
        break;
      case "road":
        setCountryRoads({
          ...countryRoads,
          isChosen: !countryRoads.isChosen,
        });
        break;
      case "city":
        setCityRoads({
          ...cityRoads,
          isChosen: !cityRoads.isChosen,
        });
        break;
      default:
        return false;
    }
  };

  return (
    <ContainerTile>
      <Wrapper>
        <H1>Fahrumgebung</H1>
      </Wrapper>
      <RowWrapper>
        <Tile>
          <Button
            width="120px"
            onClick={(e: MouseEvent<HTMLButtonElement>) =>
              onRoadClick(e.currentTarget.value)
            }
            active={cityRoads.isChosen}
            value="city"
            id="city"
          >
            Stadt
          </Button>
          {!!sliderRange && (
            <SubText style={{ marginBottom: 0 }} isActive={cityRoads.isChosen}>
              {Math.floor(cityRoads.percentage) || 0} %
            </SubText>
          )}
        </Tile>
        <Tile>
          <Button
            width="120px"
            onClick={(e: MouseEvent<HTMLButtonElement>) =>
              onRoadClick(e.currentTarget.value)
            }
            active={countryRoads.isChosen}
            value="road"
            id="road"
          >
            Landstraße
          </Button>
          {!!sliderRange && (
            <SubText
              style={{ marginBottom: 0 }}
              isActive={countryRoads.isChosen}
            >
              {Math.floor(countryRoads.percentage) || 0} %
            </SubText>
          )}
        </Tile>
        <Tile>
          <Button
            width="120px"
            onClick={(e: MouseEvent<HTMLButtonElement>) =>
              onRoadClick(e.currentTarget.value)
            }
            active={highwayRoads.isChosen}
            value="high"
            id="high"
          >
            Autobahn
          </Button>
          {!!sliderRange && (
            <SubText
              style={{ marginBottom: 0 }}
              isActive={highwayRoads.isChosen}
            >
              {Math.floor(highwayRoads.percentage) || 0} %
            </SubText>
          )}
        </Tile>
      </RowWrapper>
      {sliderRange === 2 ? (
        <RoadsSliderSingle
          setCountryRoads={setCountryRoads}
          countryRoads={countryRoads}
          setHighwayRoads={setHighwayRoads}
          highwayRoads={highwayRoads}
          setCityRoads={setCityRoads}
          cityRoads={cityRoads}
        />
      ) : sliderRange === 3 ? (
        <RoadsSliderDouble
          setCountryRoads={setCountryRoads}
          countryRoads={countryRoads}
          setHighwayRoads={setHighwayRoads}
          highwayRoads={highwayRoads}
          setCityRoads={setCityRoads}
          cityRoads={cityRoads}
        />
      ) : null}
    </ContainerTile>
  );
};

export const RoadsSliderSingle: FC<RoadsProps> = ({
  setCountryRoads,
  countryRoads,
  setHighwayRoads,
  highwayRoads,
  setCityRoads,
  cityRoads,
}) => {
  const [sliderValues, setSliderValues] = useState<number[]>([50]);

  useEffect(() => {
    if (countryRoads.isChosen) {
      setCountryRoads({ ...countryRoads, percentage: sliderValues[0] });
      if (highwayRoads.isChosen) {
        setHighwayRoads({
          ...highwayRoads,
          percentage: 100 - sliderValues[0],
        });
      } else {
        setCityRoads({
          ...cityRoads,
          percentage: 100 - sliderValues[0],
        });
      }
    } else {
      setHighwayRoads({
        ...highwayRoads,
        percentage: 100 - sliderValues[0],
      });
      setCityRoads({
        ...cityRoads,
        percentage: 100 - highwayRoads.percentage,
      });
    }
  }, [setCountryRoads, setHighwayRoads, setCityRoads, sliderValues]); //eslint-disable-line

  return (
    <SliderContainer>
      <Range
        step={1}
        min={0}
        max={100}
        values={sliderValues}
        onChange={(values_) => setSliderValues(values_)}
        renderTrack={({ props, children }) => (
          <Track {...props} color={colorPrimary}>
            {children}
          </Track>
        )}
        renderThumb={({ props }) => <Thumb {...props} />}
      />
    </SliderContainer>
  );
};

export const RoadsSliderDouble: FC<RoadsProps> = ({
  setCountryRoads,
  countryRoads,
  setHighwayRoads,
  highwayRoads,
  setCityRoads,
  cityRoads,
}) => {
  const [sliderValues, setSliderValues] = useState<number[]>([33, 66]);
  useEffect(() => {
    const roadPercent = sliderValues[1] - sliderValues[0];
    const highwayPercent = 100 - sliderValues[1];
    const cityPercent = sliderValues[0];

    setCountryRoads({ ...countryRoads, percentage: roadPercent });
    setHighwayRoads({
      ...highwayRoads,
      percentage: highwayPercent,
    });
    setCityRoads({
      ...cityRoads,
      percentage: cityPercent,
    });
  }, [setCountryRoads, setHighwayRoads, setCityRoads, sliderValues]); //eslint-disable-line

  return (
    <SliderContainer>
      <Range
        min={0}
        step={1}
        max={100}
        values={sliderValues}
        onChange={(values_) => setSliderValues(values_)}
        renderTrack={({ props, children }) => (
          <Track {...props} color={colorPrimary}>
            {children}
          </Track>
        )}
        renderThumb={({ props }) => <Thumb {...props} />}
      />
    </SliderContainer>
  );
};
